import React, { useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import moment from 'moment';
import { Dropdown } from 'primereact/dropdown';

const dateFilterFunction = (value: string, filter: any) => {
  const formatDate = (date: Date) => {
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);

    return date.getFullYear() + '-' + month + '-' + day;
  };

  if (filter === undefined || filter === null || (typeof filter === 'string' && filter.trim() === '')) {
    return true;
  }

  if (value === undefined || value === null) {
    return false;
  }

  return formatDate(new Date(value)) === formatDate(filter);
};

const DateFilterElement = ({ initialValue, onChangeFilter, name, placeholder }: any) => {
  const [value, setValue] = useState(initialValue || null);

  const onChange = (e: any) => {
    if (e.value !== value) {
      setValue(e.value);
      onChangeFilter(e.value, name);
    }
  };

  const onClickClear = () => {
    setValue(null);
    onChangeFilter(null, name);
  };

  return (
    <div style={{ position: 'relative' }}>
      <Calendar
        value={value}
        onChange={onChange}
        dateFormat="dd.mm.y"
        className="p-column-filter"
        placeholder={placeholder}
        selectionMode="range"
        footerTemplate={() => <></>}
      />
      {value ? (
        <i
          onClick={onClickClear}
          style={{ color: '#6c757d', position: 'absolute', right: '8px', top: '9px', cursor: 'pointer' }}
          className="pi pi-times"
        />
      ) : (
        ''
      )}
    </div>
  );
};

const TextFilterElement = ({ initialValue, onChangeFilter, name, placeholder }: any) => {
  const [value, setValue] = useState(initialValue || '');
  const [valueAfterChange, setValueAfterChange] = useState(initialValue || '');
  const [showClearBtn, setShowClearBtn] = useState(false);

  useEffect(() => {
    setValue(initialValue || '');
    setValueAfterChange(initialValue || '');
  }, [initialValue]);

  const checkClearBtnVisible = () => {
    if (value) {
      setShowClearBtn(true);
    } else {
      setShowClearBtn(false);
    }
  };

  useEffect(() => {
    checkClearBtnVisible();
  }, [value]);

  const onChange = (e: any) => {
    setValue(e.target.value);
  };

  const onBlur = () => {
    if (valueAfterChange !== value) {
      setValueAfterChange(value);
      onChangeFilter(value, name);
      checkClearBtnVisible();
    }
  };

  const onKeyPress = (e: any) => {
    if (e.key === 'Enter' && valueAfterChange !== value) {
      setValueAfterChange(value);
      onChangeFilter(value, name);
      checkClearBtnVisible();
    }
  };

  const onClickClear = () => {
    setValue('');
    setValueAfterChange('');
    onChangeFilter('', name);
    checkClearBtnVisible();
  };

  return (
    <div style={{ position: 'relative' }}>
      <InputText
        type="text"
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        placeholder={placeholder}
        className="p-column-filter"
        style={{ paddingRight: '24px' }}
      />
      {showClearBtn ? (
        <i
          onClick={onClickClear}
          style={{ color: '#6c757d', position: 'absolute', right: '8px', top: '9px', cursor: 'pointer' }}
          className="pi pi-times"
        />
      ) : (
        ''
      )}
    </div>
  );
};

const DropdownFilterElement = ({ initialValue, onChangeFilter, name, placeholder, options }: any) => {
  const [value, setValue] = useState(initialValue || null);

  const onChange = (e: any) => {
    const newValue = e.value !== undefined ? e.value : null;
    if (newValue !== value) {
      setValue(newValue);
      onChangeFilter(newValue, name);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Dropdown
        value={value}
        options={options}
        onChange={onChange}
        placeholder={placeholder}
        className="p-column-filter"
        showClear
      />
    </div>
  );
};

const CustomDatePicker = ({ filters, onChangeFilter, name }: any) => {
  const [startDate, setStartDate] = useState(filters.date && filters.date[0] ? filters.date[0] : undefined);
  const [endDate, setEndDate] = useState(filters.date && filters.date[1] ? filters.date[1] : undefined);

  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const [isModalVisible, setModalVisible] = useState(false);
  const [singleDateMode, setSingleDateMode] = useState(filters.singleDateMode ? filters.singleDateMode : false);
  const [invalidDateError, setInvalidDateError] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const [clearFiltersFlag, setClearFiltersFlag] = useState(false);

  const shouldClearDate = true;

  const updateDisplayValue = (firstDate: Date, secondDate: Date) => {
    if (singleDateMode) {
      setDisplayValue(firstDate ? moment(firstDate).format('DD.MM.YYYY') : '');
    }

    if (!singleDateMode && firstDate && secondDate) {
      setDisplayValue(
        `${moment(firstDate).format('DD.MM.YYYY HH:mm:ss')} - ${moment(secondDate).format('DD.MM.YYYY HH:mm:ss')}`,
      );
    }
  };

  useEffect(() => {
    setStartDate(filters.date && filters.date[0] ? filters.date[0] : undefined);
    setEndDate(filters.date && filters.date[1] ? filters.date[1] : undefined);
    updateDisplayValue(filters.date?.[0], filters.date?.[1]);
  }, [filters.date]);

  const handleClearAll = () => {
    setDisplayValue('');
    setTempStartDate(null);
    setTempEndDate(null);
    setStartDate(null);
    setEndDate(null);
    onChangeFilter(null, name, singleDateMode, shouldClearDate);
    setModalVisible(false);
  };

  useEffect(() => {
    if (tempStartDate && tempEndDate && moment(tempStartDate).isAfter(moment(tempEndDate))) {
      setInvalidDateError(true);
    } else {
      setInvalidDateError(false);
    }
  }, [tempStartDate, tempEndDate]);

  useEffect(() => {
    if (clearFiltersFlag) {
      handleClearAll();
      setClearFiltersFlag(false);
    }
  }, [clearFiltersFlag]);

  useEffect(() => {
    if (singleDateMode) {
      setEndDate(null);
      setTempEndDate(null);
    }
  }, [singleDateMode]);

  const handleConfirm = () => {
    let combinedValue = null;
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);

    if (singleDateMode && tempStartDate) {
      combinedValue = [
        tempStartDate,
        moment(tempStartDate)
          .endOf('day')
          .hours(23)
          .minutes(59)
          .seconds(59)
          .milliseconds(999)
          .format('YYYY-MM-DD HH:mm:ss.SSS'),
      ];
    }

    if (tempStartDate && tempEndDate) {
      combinedValue = [tempStartDate, tempEndDate];
    }

    onChangeFilter(combinedValue, name, singleDateMode);

    updateDisplayValue(tempStartDate, tempEndDate);
    setModalVisible(false);
  };

  const viewDateHandler = (isStartDate: boolean) => {
    const currentDate = new Date();

    currentDate.setHours(isStartDate ? 0 : 23);
    currentDate.setMinutes(isStartDate ? 0 : 59);
    currentDate.setSeconds(isStartDate ? 0 : 59);

    return currentDate;
  };

  /**TODO: its temporary solution with placeholders, if we dont fix somehow calendar bug
   * with displaying values after retrieving them from filters.date,
   * this solution is the best one we currently can have */
  const generateStartDatePlaceholder = () => {
    if (!tempStartDate) return '';

    if (singleDateMode) {
      return moment(tempStartDate).format('DD.MM.YYYY');
    }

    return moment(tempStartDate).format('DD.MM.YYYY HH:mm:ss');
  };

  const generateEndDatePlaceholder = () => {
    if (!tempEndDate) return '';

    return moment(tempEndDate).format('DD.MM.YYYY HH:mm:ss');
  };

  const modalFooter = (
    <div className="flex justify-content-between mt-4">
      <div>
        <Button
          label="Wyczyść"
          icon="pi pi-trash"
          onClick={handleClearAll}
          style={{ marginBottom: '2vh', marginTop: '2vh' }}
        />
      </div>
      <div>
        <Button label="Zamknij" icon="pi pi-times" onClick={() => setModalVisible(false)} />
        <Button
          label="Akceptuj"
          icon="pi pi-check"
          disabled={
            invalidDateError ||
            (singleDateMode && !tempStartDate) ||
            (!singleDateMode && (!tempStartDate || !tempEndDate))
          }
          onClick={handleConfirm}
          autoFocus
        />
      </div>
    </div>
  );

  return (
    <div>
      <div>
        <InputText
          value={displayValue}
          placeholder={'Wybierz datę'}
          readOnly
          onClick={() => setModalVisible(true)}
          className="p-column-filter"
        />
      </div>

      <Dialog
        header={'Wybierz datę'}
        visible={isModalVisible}
        style={{ width: '500px' }}
        footer={modalFooter}
        onHide={() => setModalVisible(false)}
      >
        <div className="pt-3 pb-5" style={{ marginBottom: '5vh' }}>
          <Checkbox
            inputId="singleDateModeCheckbox"
            onChange={(e) => setSingleDateMode(e.checked)}
            checked={singleDateMode}
          />
          <label htmlFor="singleDateModeCheckbox" style={{ margin: '1vh' }}>
            Wybierz jedną datę
          </label>
        </div>

        <div className="grid">
          <div className="col-6" style={{ marginBottom: '4vh' }}>
            <h6>{singleDateMode ? 'Data' : 'Data początkowa'}</h6>

            <Calendar
              value={tempStartDate}
              dateFormat="dd.mm.yy"
              viewDate={(!singleDateMode && viewDateHandler(true)) as any}
              onChange={(e) => setTempStartDate(e.value)}
              showTime={!singleDateMode ? true : false}
              showSeconds={!singleDateMode ? true : false}
              hourFormat="24"
              placeholder={generateStartDatePlaceholder()}
              showIcon
              showButtonBar
            />

            {invalidDateError && <div>Niepoprawna data</div>}
          </div>
          {!singleDateMode && (
            <div>
              <h6>Data końcowa</h6>
              <Calendar
                value={tempEndDate}
                dateFormat="dd.mm.yy"
                viewDate={viewDateHandler(false)}
                onChange={(e) => setTempEndDate(e.value)}
                showTime
                showSeconds
                hourFormat="24"
                placeholder={generateEndDatePlaceholder()}
                showIcon
                showButtonBar
                className={invalidDateError ? 'p-invalid' : ''}
              />
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

const DataTableFilterElements = {
  Date: DateFilterElement,
  Text: TextFilterElement,
  Dropdown: DropdownFilterElement,
  dateFilterFunction,
  CustomDatePicker,
};

export default DataTableFilterElements;
